import type { ListBuilderConfiguration } from '../types';

/**
 * Issue list configuration
 * Main issues list with full features including filtering, sorting, and actions
 * Converted from JSON to TypeScript for type safety
 */
export const ISSUE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-issues-all',
			cacheTTL: 600000,
			name: 'All Issues',
			type: 'default',
			active: true,
			sortOrder: 0,
			filterRules: {
				issueStatus: {
					defaultValue: [
						{ id: 'To-Do', label: 'To-Do' },
						{ id: 'In Progress', label: 'In Progress' },
					],
				},
				issueAssignee: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: false },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
				},
				issueClientSalesRep: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'issues:view:any', negated: true },
						],
					},
				},
			},
			columns: [
				{
					id: 'issue-name-type',
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'issue',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'name', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'md',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'type-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'type', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							// PD-15442: where the issue came from ("CRM" or "Customer
							// Portal"). Not a stored column — IssueService derives the
							// label per row from the portal submitter foreign key, the same
							// column the `issueSource` filter tests, so the two cannot
							// disagree. It rides in the Name column as plain text (owner,
							// 2026-09-16) rather than as its own column, to keep the row
							// narrow; it stays selectable as a standalone field below.
							id: 'source-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'source', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					label: 'Name',
					headers: [{ id: 'h2', text: 'Name' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{ direction: 'ASC', fieldName: 'name', objectTable: 'issue' },
					],
					width: 'w-32',
				},
				{
					id: 'issue-status',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'status', objectTable: 'issue' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'issue-status-selector' },
						},
					],
					label: 'Status',
					headers: [{ id: 'h1', text: 'Status' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{ direction: 'ASC', fieldName: 'status', objectTable: 'issue' },
					],
					width: 'w-16',
				},
				{
					id: 'issue-priority',
					fields: [
						{
							id: 'priority-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'priority', objectTable: 'issue' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'issue-priority-selector' },
						},
					],
					label: 'Priority',
					headers: [{ id: 'h-priority', text: 'Priority' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					// PD-14789: priority first, then newest. Priority alone leaves ties
					// in insertion order, so an issue created from a record landed at
					// the bottom of its list, off-screen.
					sortFields: [
						{ direction: 'DESC', fieldName: 'priority', objectTable: 'issue' },
						{
							direction: 'DESC',
							fieldName: 'createdDate',
							objectTable: 'issue',
						},
					],
					width: 'w-24',
				},
				{
					id: 'issue-owner',
					fields: [
						{
							id: 'owner-name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'assigneeUserName', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					label: 'Assigned To',
					headers: [{ id: 'h-owner', text: 'Assigned To' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					// PD-15834 — must match the virtual sort field the API handles
					// (IssueService.getIssues orders on the joined assignee's name).
					// This previously read `ownerUserName`, copied from the sourcing
					// request list; issues have an assignee, not an owner, so the
					// server emitted `ORDER BY issue.ownerUserName` and the whole
					// list failed with an unknown-column error.
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'assigneeUserName',
							objectTable: 'issue',
						},
					],
					width: 'w-28',
				},
				{
					id: 'issue-description',
					fields: [
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								textExpansion: { enabled: true, maxLines: 3 },
							},
							displayStyle: 'text',
						},
					],
					label: 'Description',
					headers: [{ id: 'h3', text: 'Description' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					width: 'flex-1',
				},
				{
					id: 'issue-created-date',
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'createdDate', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
						},
						{
							id: 'created-line-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							// PD-14524 — creator name shown as a subtitle beneath the
							// created date, under the single "Created" header.
							id: 'created-by-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'creatorUserName', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					label: 'Created',
					headers: [{ id: 'h-created', text: 'Created' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_created_date',
							objectTable: 'issue',
						},
					],
					width: 'w-32',
				},
				{
					id: 'related-entities',
					maxDisplay: 2,
					fields: [
						{
							id: 'related-clients',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'clients', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'related_entity_list',
							displayConfig: {
								entityType: 'client',
								icon: 'users',
								linkPattern: '/clients/{id}',
								nameField: 'name',
								separator: 'line_break',
							},
						},
						{
							id: 'related-vendors',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'vendors', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'related_entity_list',
							displayConfig: {
								entityType: 'vendor',
								icon: 'truck',
								linkPattern: '/vendors/{id}',
								nameField: 'name',
								separator: 'line_break',
							},
						},
						{
							id: 'related-merchandisers',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'merchandisers', objectTable: 'issue' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'related_entity_list',
							displayConfig: {
								entityType: 'merchandiser',
								icon: 'merchandiser',
								linkPattern: '/distribution/merchandisers/{id}',
								nameField: 'name',
								separator: 'line_break',
							},
						},
					],
					label: 'Related To',
					headers: [{ id: 'h4', text: 'Related To' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					width: 'w-48',
				},
				{
					id: 'issue-actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					label: 'Actions',
					headers: [{ id: 'h-actions', text: '' }],
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					width: 'w-24',
				},
			],
			defaultSortId: 'column-issue-priority-desc',
			clickAction: {
				type: 'navigate',
				path: '/issues/{id}',
			},
			actions: [
				{
					id: 'create-issue',
					label: 'Create Issue',
					icon: 'plus',
					type: 'create',
					position: 'header',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'issueKeywords',
			'issueStatus',
			'issueType',
			'issuePriority',
			'issueSource',
			'issueClientSalesRep',
			'issueClient',
			'issueAssignee',
			'issueCreatedDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/issues/:id',
			objectLabel: 'Issue',
			objectTable: 'issue',
			// Rows on this list are issue records keyed by `id`, so any
			// configured column of the table can be filled in server-side
			// (ListFieldHydrationService).
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'priority', fieldLabel: 'Priority' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
				{ fieldName: '_modified_date', fieldLabel: 'Modified Date' },
				{ fieldName: 'assigneeUserName', fieldLabel: 'Assigned To' },
				{ fieldName: 'creatorUserName', fieldLabel: 'Created By' },
				// PD-15442 — derived by the API from the portal submitter FK, not a
				// stored column on `issue`.
				{ fieldName: 'source', fieldLabel: 'Source' },
			],
		},
	],
};
